import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { SignaturePad, MethodTabs } from './SignPlanOfCarePage';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const Card = ({ children }) => <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl">{children}</div>;

// Public page: the doctor reviews the order sheet and e-signs it
export default function SignOrderPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState('');
  const [method, setMethod] = useState('drawn');
  const [image, setImage] = useState(null);
  const [typed, setTyped] = useState('');
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  useEffect(() => {
    fetch(`${API_URL}/api/order-sign/${token}`).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.detail || 'This link is not valid'); return; }
      setInfo(d); setTyped(d.physicianName || '');
      const p = await fetch(`${API_URL}/api/order-sign/${token}/pages`).then(x => x.json());
      setPages(p.pages || []);
    }).catch(() => setError('Could not reach the server'));
  }, [token]);
  const submit = async () => {
    setBusy(true); setError('');
    const r = await fetch(`${API_URL}/api/order-sign/${token}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method, typedName: method === 'typed' ? typed : info.physicianName, signatureImage: image, attest }) });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok) setDone(d.message); else setError(d.detail || 'Signing failed');
  };
  const ready = attest && (method === 'drawn' ? !!image : typed.trim().length >= 3);
  return (
    <div className="min-h-screen bg-gray-950 px-4 py-6 sm:py-10" data-testid="order-sign-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <header>
          <p className="text-[#d4af37] text-xs tracking-widest uppercase">{info?.agency || 'IVORY Home Health Care'}</p>
          <h1 className="text-3xl font-bold text-white mt-1">{info?.orderType || 'Physician Order'}</h1>
          {info && <p className="text-gray-400 text-sm mt-1">Patient: {info.patientName}</p>}
        </header>
        {done ? <Card><div className="text-center py-6" data-testid="order-sign-success"><CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" /><h2 className="text-2xl font-bold">Signed</h2><p className="text-gray-600">{done}</p></div></Card> : <>
          {error && <Card><p className="flex items-center gap-2 text-red-700" data-testid="order-sign-error"><AlertTriangle className="w-5 h-5" /> {error}</p></Card>}
          {info && <Card>
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border bg-gray-50 space-y-2 p-1" data-testid="order-sign-preview">
              {pages.length ? pages.map((p, i) => <img key={i} src={`data:image/png;base64,${p}`} alt={`Order page ${i + 1}`} className="w-full bg-white" />)
                : <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}
            </div>
            <a href={`${API_URL}/api/order-sign/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-block mt-2 text-sm text-[#b8941f] underline" data-testid="order-sign-open-pdf">Open full PDF</a>
          </Card>}
          {info && <Card>
            <h2 className="text-lg font-semibold mb-3">Sign as {info.physicianName}</h2>
            <MethodTabs method={method} setMethod={setMethod} />
            {method === 'drawn' ? <SignaturePad onChange={setImage} /> : <input value={typed} onChange={e => setTyped(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg" data-testid="order-sign-typed" />}
            <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
              <input type="checkbox" checked={attest} onChange={e => setAttest(e.target.checked)} className="mt-1 w-4 h-4" data-testid="order-sign-attest" />
              I have reviewed this order, I authorize it, and I am applying my electronic signature.
            </label>
            <button onClick={submit} disabled={!ready || busy} className="mt-4 w-full py-3 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40 flex items-center justify-center gap-2" data-testid="order-sign-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign Order
            </button>
          </Card>}
        </>}
      </div>
    </div>
  );
}
